/**
 * Theme Utilities
 *
 * Utility functions for color manipulation, validation, and theme operations.
 */

/**
 * Convert hex color to RGB
 * @param {string} hex - Hex color code
 * @returns {object} RGB values {r, g, b}
 */
export const hexToRgb = hex => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

/**
 * Convert RGB to hex
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {string} Hex color code
 */
export const rgbToHex = (r, g, b) => {
  return (
    '#' +
    [r, g, b]
      .map(x => {
        const hex = x.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      })
      .join('')
  );
};

/**
 * Adjust color opacity
 * @param {string} color - Hex color code
 * @param {number} opacity - Opacity value (0-1)
 * @returns {string} RGBA color string
 */
export const adjustOpacity = (color, opacity) => {
  const rgb = hexToRgb(color);
  if (!rgb) return color;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
};

/**
 * Lighten a color by a percentage
 * @param {string} color - Hex color code
 * @param {number} percent - Percentage to lighten (0-100)
 * @returns {string} Lightened hex color
 */
export const lightenColor = (color, percent) => {
  const rgb = hexToRgb(color);
  if (!rgb) return color;

  const amount = Math.round((255 * percent) / 100);
  const r = Math.min(255, rgb.r + amount);
  const g = Math.min(255, rgb.g + amount);
  const b = Math.min(255, rgb.b + amount);

  return rgbToHex(r, g, b);
};

/**
 * Darken a color by a percentage
 * @param {string} color - Hex color code
 * @param {number} percent - Percentage to darken (0-100)
 * @returns {string} Darkened hex color
 */
export const darkenColor = (color, percent) => {
  const rgb = hexToRgb(color);
  if (!rgb) return color;

  const amount = Math.round((255 * percent) / 100);
  const r = Math.max(0, rgb.r - amount);
  const g = Math.max(0, rgb.g - amount);
  const b = Math.max(0, rgb.b - amount);

  return rgbToHex(r, g, b);
};

/**
 * Derive status color from base color
 * @param {string} baseColor - Base color to derive from
 * @param {string} type - Status type ('success', 'warning', 'error')
 * @returns {string} Derived color
 */
export const deriveStatusColor = (baseColor, type) => {
  const rgb = hexToRgb(baseColor);
  if (!rgb) {
    // Fallback to default status colors
    const defaults = {
      success: '#2AC460', // Vibrant green - Completed Executions card
      warning: '#FFA02A', // Bright orange - Total Cost card
      error: '#f44336',
      info: '#2AC0C4', // Teal/Cyan - Success Rate card
    };
    return defaults[type] || baseColor;
  }

  switch (type) {
    case 'success':
      // Shift towards green
      return rgbToHex(
        Math.max(0, rgb.r - 30),
        Math.min(255, rgb.g + 50),
        Math.max(0, rgb.b - 30)
      );
    case 'warning':
      // Shift towards orange
      return rgbToHex(
        Math.min(255, rgb.r + 50),
        Math.min(255, rgb.g + 30),
        Math.max(0, rgb.b - 50)
      );
    case 'error':
      // Shift towards red
      return rgbToHex(
        Math.min(255, rgb.r + 50),
        Math.max(0, rgb.g - 50),
        Math.max(0, rgb.b - 50)
      );
    case 'info':
      // Shift towards blue
      return rgbToHex(
        Math.max(0, rgb.r - 30),
        Math.max(0, rgb.g - 30),
        Math.min(255, rgb.b + 50)
      );
    default:
      return baseColor;
  }
};

/**
 * Calculate color contrast ratio (WCAG)
 * @param {string} color1 - First color (hex)
 * @param {string} color2 - Second color (hex)
 * @returns {number} Contrast ratio
 */
export const getContrastRatio = (color1, color2) => {
  const getLuminance = color => {
    const rgb = hexToRgb(color);
    if (!rgb) return 0;

    const rsRGB = rgb.r / 255;
    const gsRGB = rgb.g / 255;
    const bsRGB = rgb.b / 255;

    const r =
      rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
    const g =
      gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
    const b =
      bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);

  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);

  return (brightest + 0.05) / (darkest + 0.05);
};

/**
 * Check if color meets WCAG AA standard
 * @param {string} foreground - Foreground color (hex)
 * @param {string} background - Background color (hex)
 * @returns {boolean} True if meets AA standard
 */
export const meetsWCAGAA = (foreground, background) => {
  const ratio = getContrastRatio(foreground, background);
  return ratio >= 4.5; // AA standard for normal text
};

/**
 * Check if color meets WCAG AAA standard
 * @param {string} foreground - Foreground color (hex)
 * @param {string} background - Background color (hex)
 * @returns {boolean} True if meets AAA standard
 */
export const meetsWCAGAAA = (foreground, background) => {
  const ratio = getContrastRatio(foreground, background);
  return ratio >= 7.0; // AAA standard for normal text
};

/**
 * Validate hex color format
 * @param {string} color - Color string to validate
 * @returns {boolean} True if valid hex color
 */
export const isValidHexColor = color => {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
};

/**
 * Get appropriate text color (black or white) for a background
 * @param {string} backgroundColor - Background color (hex)
 * @returns {string} Text color (hex)
 */
export const getTextColorForBackground = backgroundColor => {
  const whiteContrast = getContrastRatio('#ffffff', backgroundColor);
  const blackContrast = getContrastRatio('#000000', backgroundColor);

  return whiteContrast > blackContrast ? '#ffffff' : '#000000';
};

/**
 * Parse font size string to number
 * @param {string|number} fontSize - Font size (e.g., '14px', '1rem', 14)
 * @returns {number} Font size in pixels
 */
export const parseFontSize = fontSize => {
  if (typeof fontSize === 'number') return fontSize;
  if (!fontSize) return 14;

  const match = fontSize.match(/^(\d+(?:\.\d+)?)(px|rem|em)?$/);
  if (!match) return 14;

  const value = parseFloat(match[1]);
  const unit = match[2] || 'px';

  switch (unit) {
    case 'rem':
    case 'em':
      return value * 16; // Assume 16px base
    case 'px':
    default:
      return value;
  }
};

/**
 * Format font size for CSS
 * @param {number} size - Font size in pixels
 * @param {string} unit - Unit to use ('px' or 'rem')
 * @returns {string} Formatted font size
 */
export const formatFontSize = (size, unit = 'px') => {
  if (unit === 'rem') {
    return `${size / 16}rem`;
  }
  return `${size}px`;
};

/**
 * Validate theme data
 * @param {object} themeData - Theme data to validate
 * @returns {object} Validation result {isValid, errors}
 */
export const validateThemeData = themeData => {
  const errors = [];

  // Validate required fields
  if (!themeData.theme_name || themeData.theme_name.trim() === '') {
    errors.push('Theme name is required');
  }

  // Validate colors
  const colorFields = [
    'primary_color',
    'secondary_color',
    'accent_color',
    'background_color',
    'surface_color',
    'text_color',
  ];

  colorFields.forEach(field => {
    if (themeData[field] && !isValidHexColor(themeData[field])) {
      errors.push(`${field} must be a valid hex color`);
    }
  });

  // Validate numeric fields
  if (themeData.border_radius && themeData.border_radius < 0) {
    errors.push('Border radius must be non-negative');
  }

  if (themeData.spacing_unit && themeData.spacing_unit < 4) {
    errors.push('Spacing unit must be at least 4px');
  }

  // Validate contrast
  if (
    themeData.text_color &&
    themeData.background_color &&
    !meetsWCAGAA(themeData.text_color, themeData.background_color)
  ) {
    errors.push('Text color does not meet WCAG AA contrast requirements');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

const utils = {
  hexToRgb,
  rgbToHex,
  adjustOpacity,
  lightenColor,
  darkenColor,
  deriveStatusColor,
  getContrastRatio,
  meetsWCAGAA,
  meetsWCAGAAA,
  isValidHexColor,
  getTextColorForBackground,
  parseFontSize,
  formatFontSize,
  validateThemeData,
};

export default utils;
